import { api } from './axios';
import type { Bookmark, Collection, CollectionWithCount, Tag, ShareLink, PublicShareResponse } from '@/types/database';

// Extended bookmark type for API requests (includes tags as string array)
type BookmarkInput = Partial<Bookmark> & { tags?: string[] };

// Collections API
export const collectionsApi = {
  async getAll(): Promise<CollectionWithCount[]> {
    const { data } = await api.get('/collections');
    // Add default bookmark_count if not present
    return data.map((collection: Collection) => ({
      ...collection,
      bookmark_count: (collection as CollectionWithCount).bookmark_count ?? 0,
    }));
  },

  async create(collection: Partial<Collection>): Promise<Collection> {
    const { data } = await api.post('/collections', collection);
    return data;
  },

  async update(id: string, updates: Partial<Collection>): Promise<Collection> {
    const { data } = await api.put(`/collections/${id}`, updates);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/collections/${id}`);
  },
};

// Bookmarks API
export const bookmarksApi = {
  async getAll(params?: {
    collection_id?: string;
    search?: string;
    archived?: boolean;
  }): Promise<Bookmark[]> {
    const { data } = await api.get('/bookmarks', { params });
    return data;
  },

  async create(bookmark: BookmarkInput): Promise<Bookmark> {
    const { data } = await api.post('/bookmarks', bookmark);
    return data;
  },

  async update(id: string, updates: BookmarkInput): Promise<Bookmark> {
    const { data } = await api.put(`/bookmarks/${id}`, updates);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/bookmarks/${id}`);
  },
};

// Tags API
export const tagsApi = {
  async getAll(): Promise<Tag[]> {
    const { data } = await api.get('/tags');
    return data;
  },
};

// Metadata API
export const metadataApi = {
  async extract(url: string) {
    const { data } = await api.post('/metadata', { url });
    return data;
  },
};

// Semantic Search API
export interface SemanticSearchResult {
  bookmarks: (Bookmark & { _similarity: number })[];
  query: string;
  total: number;
  message?: string;
}

export interface SearchIndexStatus {
  total_bookmarks: number;
  indexed_bookmarks: number;
  pending_bookmarks: number;
  indexing_progress: number;
}

export interface MigrationResult {
  total: number;
  processed: number;
  success: number;
  failed: number;
  skipped: number;
  errors: string[];
}

export const semanticSearchApi = {
  /**
   * Search bookmarks using semantic similarity
   * @param query - Search query text
   * @param options - Search options
   */
  async search(
    query: string,
    options: { threshold?: number; limit?: number } = {}
  ): Promise<SemanticSearchResult> {
    const params = new URLSearchParams({ q: query });
    if (options.threshold !== undefined) {
      params.set('threshold', options.threshold.toString());
    }
    if (options.limit !== undefined) {
      params.set('limit', options.limit.toString());
    }
    const { data } = await api.get(`/search/semantic?${params.toString()}`);
    return data;
  },

  /**
   * Get indexing status for current user
   */
  async getStatus(): Promise<SearchIndexStatus> {
    const { data } = await api.get('/search/status');
    return data;
  },

  /**
   * Migrate existing bookmarks to have embeddings
   * Note: This may take a while for users with many bookmarks
   */
  async migrate(): Promise<MigrationResult> {
    const { data } = await api.post('/search/migrate');
    return data;
  },

  /**
   * Generate embedding for a text (for debugging)
   */
  async generateEmbedding(text: string): Promise<{
    model: string;
    dimensions: number;
    tokens?: number;
    embedding_preview: number[];
  }> {
    const { data } = await api.post('/search/embed', { text });
    return data;
  },
};

// Shares API (protected - requires auth)
export const sharesApi = {
  async getAll(): Promise<ShareLink[]> {
    const { data } = await api.get('/shares');
    return data;
  },

  async getById(id: string): Promise<ShareLink> {
    const { data } = await api.get(`/shares/${id}`);
    return data;
  },

  async getByResource(
    resourceType: 'collection' | 'bookmark',
    resourceId: string
  ): Promise<{ shared: boolean; share?: ShareLink }> {
    const { data } = await api.get(`/shares/resource/${resourceType}/${resourceId}`);
    return data;
  },

  async create(params: {
    resource_type: 'collection' | 'bookmark';
    resource_id: string;
    slug?: string;
    allow_copy?: boolean;
    password?: string;
  }): Promise<ShareLink> {
    const { data } = await api.post('/shares', params);
    return data;
  },

  async update(
    id: string,
    updates: Partial<{
      slug: string | null;
      is_active: boolean;
      allow_copy: boolean;
      password: string | null;
    }>
  ): Promise<ShareLink> {
    const { data } = await api.put(`/shares/${id}`, updates);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/shares/${id}`);
  },
};

// Public API (no auth required)
export const publicApi = {
  async getShare(shareId: string): Promise<PublicShareResponse> {
    const { data } = await api.get(`/public/s/${shareId}`);
    return data;
  },

  async verifyPassword(
    shareId: string,
    password: string
  ): Promise<PublicShareResponse & { verified: boolean }> {
    const { data } = await api.post(`/public/s/${shareId}/verify`, { password });
    return data;
  },

  async verifyConnectionCode(
    code: string
  ): Promise<{ access_token: string; refresh_token: string; expires_at?: number }> {
    const { data } = await api.post('/public/connection-codes/verify', { code });
    return data;
  },
};

// Connection Codes API (for extension authentication)
export const connectionCodesApi = {
  async create(session: {
    access_token: string;
    refresh_token: string;
    expires_at?: number;
  }): Promise<{ code: string }> {
    const { data } = await api.post('/connection-codes', session);
    return data;
  },
};
